import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldString,
} from '@shared/types/process';

import type { MaskitoOptions } from '@maskito/core';
import { maskitoPhoneOptionsGenerator } from '@maskito/phone';
import { ShadcnForm } from '@shadcn-features/core';
import { ShadcnFormInputProps } from '@shadcn-features/core/ShadcnForm/fields';
import { ShadcnFormRule } from '@shadcn-features/core/ShadcnForm/types';
import metadata from 'libphonenumber-js/min/metadata';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends ShadcnFormInputProps {
    field: ProcessFieldBase & ProcessFieldString;
}

export function ProcessFieldStringField({
    field,
    fieldProps,
    ...props
}: Props) {
    const { t } = useTranslation();
    const getLangText = useLangText();

    const type = field.options.patternDefault ?? 'text';
    const regex = useMemo(() => {
        try {
            return new RegExp(field.options.pattern || '');
        } catch (e) {
            console.debug('Invalid regex pattern:', field.options.pattern, e);
            return new RegExp('.*');
        }
    }, [field.options.pattern]);

    const mask: MaskitoOptions | undefined = useMemo(() => {
        if (field.options.patternDefault === 'tel') {
            return maskitoPhoneOptionsGenerator({
                strict: true,
                countryIsoCode: 'KZ',
                separator: ' ',
                metadata,
            });
        }

        return undefined;
    }, [field.options.patternDefault]);

    const rules = useMemo(() => {
        const rules: ShadcnFormRule = [];
        if (field.options.patternDefault === 'email') {
            rules.push({
                type: 'email',
                message: t('translation:invalidEmailFormat'),
            });
        }
        if (field.options.minLength) {
            rules.push({
                min: field.options.minLength,
                message: t('translation:minLengthMustBe', {
                    minLength: field.options.minLength,
                }),
            });
        }

        if (field.options.maxLength) {
            rules.push({
                max: field.options.maxLength,
                message: t('translation:maxLengthMustBe', {
                    maxLength: field.options.maxLength,
                }),
            });
        }

        if (field.options.pattern) {
            rules.push({
                pattern: regex,
                message: t('translation:invalidPattern', {
                    pattern: field.options.pattern,
                }),
            });
        }

        return rules;
    }, [
        field.options.maxLength,
        field.options.minLength,
        field.options.pattern,
        field.options.patternDefault,
        regex,
        t,
    ]);

    return (
        <ShadcnForm.Input
            fieldProps={{ ...fieldProps }}
            maskito={mask}
            type={type}
            {...props}
            rules={rules.concat(props?.rules ?? [])}
            tooltip={getLangText(field?.tooltip ?? '')}
        ></ShadcnForm.Input>
    );
}
